/**
 * Volume validation for the C3.b fuel step — a 1:1 port of Android's `OrderStep.kt`
 * (`validateLiters`, `LitersValidation`) and the constants around it.
 *
 * **Why thousandths instead of a decimal type.** Kotlin uses `BigDecimal` and iOS uses `Decimal`;
 * JavaScript has neither, and `15.5 + 12.3` is `27.800000000000004` in floating point. Every
 * volume here is therefore held as an integer number of **millilitres**, so sums and comparisons
 * are exact, and turned back into a decimal string only at the edges. This matters beyond
 * tidiness: the city minimum is checked against a *sum* across cars, and a drifting last digit
 * would refuse an order that exactly meets the threshold.
 *
 * **Two parsers, on purpose.** A number a person typed may use the RU keyboard's comma; a number
 * that came off the wire never does. Mixing them would quietly accept `"55,000"` from a DTO as
 * fifty-five when the server meant fifty-five thousand — Android keeps the same separation.
 */

/** Volume as an integer count of millilitres. */
export type Milli = number;

export const MILLI = 1000;

/** Only a plain decimal. Rejects `1e2`, `0x10`, `30.` and anything with a stray character. */
const WIRE_DECIMAL = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

/**
 * Smallest orderable volume.
 *
 * The backend's own floor is merely "greater than zero"; a whole litre is the smallest amount
 * worth sending an operator across town for, and iOS has always enforced exactly this. Android
 * accepted «0,3 л» until the 2026-08-18 audit.
 */
export const MIN_ORDERABLE_LITERS_MILLI: Milli = 1 * MILLI;

/**
 * Ceiling for a car with no declared tank volume.
 *
 * The same number the backend uses (`FALLBACK_MAX_ESTIMATED_LITERS`) — deliberately not a second
 * opinion, because a client that guessed differently would let through an order the server then
 * refuses.
 */
export const FALLBACK_MAX_LITERS_MILLI: Milli = 300 * MILLI;

/**
 * Typical passenger-car tank, used only for «до полного бака» on a car with no declared volume.
 *
 * A hold estimate, not a promise: the real charge at finish is always the litres actually poured.
 */
export const FALLBACK_ESTIMATED_FULL_TANK_LITERS = '50';

/** Parses a decimal string as it arrives from the API — dot only, no locale accommodation. */
export function parseWireLiters(raw: string | null | undefined): Milli | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (!WIRE_DECIMAL.test(trimmed)) return null;
  const value = Number.parseFloat(trimmed);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * MILLI);
}

/** Parses what a person typed, accepting the RU keyboard's comma as the decimal separator. */
export function parseTypedLiters(raw: string): Milli | null {
  return parseWireLiters(raw.replace(',', '.'));
}

/** Back to a wire-shaped decimal string: `27800` → `"27.8"`, `50000` → `"50"`. */
export function litersToWire(value: Milli): string {
  const whole = Math.trunc(value / MILLI);
  const fraction = Math.abs(value % MILLI);
  if (fraction === 0) return String(whole);
  const sign = value < 0 && whole === 0 ? '-' : '';
  return `${sign}${whole}.${String(fraction).padStart(3, '0').replace(/0+$/, '')}`;
}

/**
 * Every way {@link validateLiters} can resolve — a closed set, so the fuel step handles each
 * case explicitly instead of branching on one opaque boolean.
 */
export type LitersValidation =
  | { kind: 'valid'; liters: Milli }
  /** Nothing typed yet. Distinct from `notANumber` so the field stays neutral before first input. */
  | { kind: 'blank' }
  | { kind: 'notANumber' }
  /** Below {@link MIN_ORDERABLE_LITERS_MILLI} — zero, negative, or a fraction of a litre. */
  | { kind: 'notPositive' }
  /** More than this car's tank holds; `cap` is its declared volume or the shared fallback. */
  | { kind: 'exceedsCapacity'; cap: Milli }
  /** Above the city's own per-order ceiling — a different refusal: the tank would take it, this выезд may not. */
  | { kind: 'exceedsCityMax'; max: Milli };

/**
 * Validates the «Указать литры» free-text field.
 *
 * `tankVolumeLiters` is the selected car's declared capacity when known; `cityMaxLiters` is the
 * resolved city's per-order ceiling when the backend sent one.
 *
 * The city's **minimum** is deliberately absent: an order below it is accepted and billed at the
 * minimum, so it is a line the step renders, never a validation failure (owner decision
 * 2026-08-19). See `minOrderStatus`.
 */
export function validateLiters(
  raw: string,
  tankVolumeLiters: string | null | undefined,
  cityMaxLiters?: string | null,
): LitersValidation {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: 'blank' };

  const parsed = parseTypedLiters(trimmed);
  if (parsed === null) return { kind: 'notANumber' };
  if (parsed < MIN_ORDERABLE_LITERS_MILLI) return { kind: 'notPositive' };

  const cap = parseWireLiters(tankVolumeLiters) ?? FALLBACK_MAX_LITERS_MILLI;
  if (parsed > cap) return { kind: 'exceedsCapacity', cap };

  // The city ceiling is checked after the tank's: when both are exceeded, «в этот бак не войдёт»
  // is the more actionable of the two — it names something the person can see.
  const cityMax = parseWireLiters(cityMaxLiters);
  if (cityMax !== null && parsed > cityMax) return { kind: 'exceedsCityMax', max: cityMax };

  return { kind: 'valid', liters: parsed };
}
